import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader,
  User,
  Calendar,
  Building2,
  AlertTriangle,
  Star,
  Globe,
  FileText,
  Settings,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import Modal from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchInvoiceById,
  fetchClients,
  fetchUsage,
  fetchSellers,
  fetchInvoiceSettings,
  createInvoice,
  updateInvoice,
  createClient,
  setCurrentInvoice
} from '../../store/invoiceSlice';
import { getNextInvoiceNumber } from '../../api/invoices';
import { InvoiceItem, ClientCreateData, InvoiceType, InvoiceLanguage } from '../../types';
import { getBilingualTranslation, getInvoiceTypeTitle, TranslationKey } from '../../utils/invoiceTranslations';

interface FormItem {
  description: string;
  quantity: number;
  unit_price: number;
  unit?: string;
  tax_rate?: number;
}

const INVOICE_TYPES: { value: InvoiceType; label: string }[] = [
  { value: 'standard', label: 'Standard Invoice' },
  { value: 'vat', label: 'VAT Invoice' },
  { value: 'reverse_charge', label: 'Invoice Reverse Charge VAT' },
  { value: 'proforma', label: 'Proforma Invoice' },
  { value: 'credit_note', label: 'Credit Note' }
];

const LANGUAGES: { value: InvoiceLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polish' },
  { value: 'ro', label: 'Romanian' }
];

const CURRENCIES = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'PLN', label: 'PLN (zł)', symbol: 'zł' },
  { value: 'RON', label: 'RON (lei)', symbol: 'lei' },
  { value: 'CAD', label: 'CAD ($)', symbol: '$' },
  { value: 'AUD', label: 'AUD ($)', symbol: '$' }
];

const UNITS = [
  { value: '', label: '-' },
  { value: 'pcs', label: 'pcs' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
  { value: 'kg', label: 'kg' },
  { value: 'm', label: 'm' },
  { value: 'm²', label: 'm²' },
  { value: 'm³', label: 'm³' },
  { value: 'L', label: 'L' },
  { value: 'set', label: 'set' },
  { value: 'pack', label: 'pack' },
  { value: 'box', label: 'box' },
  { value: 'unit', label: 'unit' },
  { value: 'service', label: 'service' },
  { value: 'daily_rate', label: 'daily rate' }
];

const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

const PAYMENT_TERMS_OPTIONS = [
  { value: 'immediate', label: 'Immediate payment / Płatność natychmiastowa' },
  { value: 'transfer_7', label: 'Transfer within 7 days / Przelew w terminie 7 dni' },
  { value: 'transfer_14', label: 'Transfer within 14 days / Przelew w terminie 14 dni' },
  { value: 'transfer_21', label: 'Transfer within 21 days / Przelew w terminie 21 dni' },
  { value: 'transfer_30', label: 'Transfer within 30 days / Przelew w terminie 30 dni' },
  { value: 'transfer_45', label: 'Transfer within 45 days / Przelew w terminie 45 dni' },
  { value: 'transfer_60', label: 'Transfer within 60 days / Przelew w terminie 60 dni' },
  { value: 'other', label: 'Other / Inne' },
];

// Map from dropdown value to the text stored in the DB
const PAYMENT_TERMS_TEXT: Record<string, string> = {
  immediate: 'Immediate payment / Płatność natychmiastowa',
  transfer_7: 'Transfer within 7 days / Przelew w terminie 7 dni',
  transfer_14: 'Transfer within 14 days / Przelew w terminie 14 dni',
  transfer_21: 'Transfer within 21 days / Przelew w terminie 21 dni',
  transfer_30: 'Transfer within 30 days / Przelew w terminie 30 dni',
  transfer_45: 'Transfer within 45 days / Przelew w terminie 45 dni',
  transfer_60: 'Transfer within 60 days / Przelew w terminie 60 dni',
};

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const dispatch = useAppDispatch();
  const toast = useToast();
  const { currentInvoice, clients, sellers, invoiceSettings, usage, loading, error } = useAppSelector(
    (state) => state.invoices
  );

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  // New invoices: what /next-number previewed. The preview reserves nothing,
  // so an untouched field is sent empty and the server assigns the next free
  // number at save time - another tab may have taken this one by then.
  const [suggestedNumber, setSuggestedNumber] = useState('');
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('standard');
  const [language, setLanguage] = useState<InvoiceLanguage>('en');
  const [secondaryLanguage, setSecondaryLanguage] = useState<InvoiceLanguage | ''>('');
  const [sellerId, setSellerId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [saleDate, setSaleDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [placeOfIssue, setPlaceOfIssue] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  // Derived, not stored. A reverse-charge invoice at 0% *is* the tax-exempt case
  // — there is no separate column for it — so deriving keeps the form and the
  // saved invoice from disagreeing after a reload.
  const taxExempt = invoiceType === 'reverse_charge' && taxRate === 0;
  const [discount, setDiscount] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [paymentTermsKey, setPaymentTermsKey] = useState('transfer_30');
  const [paymentTermsCustom, setPaymentTermsCustom] = useState('');
  const [showReverseChargeNotice, setShowReverseChargeNotice] = useState(false);
  const [items, setItems] = useState<FormItem[]>([
    { description: '', quantity: 1, unit_price: 0 }
  ]);

  // Translation helper - updates live when language changes
  const t = (key: TranslationKey) => getBilingualTranslation(key, language, secondaryLanguage);

  // New client modal state
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientIsBusiness, setNewClientIsBusiness] = useState(true);
  const [newClientTaxId, setNewClientTaxId] = useState('');
  const [newClientStreet, setNewClientStreet] = useState('');
  const [newClientStreetNumber, setNewClientStreetNumber] = useState('');
  const [newClientCity, setNewClientCity] = useState('');
  const [newClientPostalCode, setNewClientPostalCode] = useState('');
  const [newClientCountry, setNewClientCountry] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  // Ctrl+S calls requestSubmit(), which ignores the disabled button; a second
  // press mid-save would otherwise create a second invoice with its own number.
  const submittingRef = useRef(false);

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    dispatch(fetchClients({ favoritesFirst: true }));
    dispatch(fetchSellers(true));
    dispatch(fetchInvoiceSettings());
    dispatch(fetchUsage());

    if (isEditing && id) {
      dispatch(fetchInvoiceById(parseInt(id)));
    } else {
      getNextInvoiceNumber()
        .then((next) => {
          setSuggestedNumber(next);
          // Don't overwrite anything typed while the request was in flight.
          setInvoiceNumber((current) => current || next);
        })
        .catch(console.error);
    }

    return () => {
      dispatch(setCurrentInvoice(null));
    };
  }, [dispatch, id, isEditing]);

  // Apply defaults from settings for new invoices
  useEffect(() => {
    if (!isEditing && invoiceSettings) {
      if (invoiceSettings.default_seller_id) {
        setSellerId(invoiceSettings.default_seller_id);
      }
      if (invoiceSettings.default_currency) {
        setCurrency(invoiceSettings.default_currency);
      }
      if (invoiceSettings.default_tax_rate !== undefined) {
        setTaxRate(invoiceSettings.default_tax_rate);
      }
      if (invoiceSettings.default_language) {
        setLanguage(invoiceSettings.default_language);
      }
      if (invoiceSettings.default_secondary_language) {
        setSecondaryLanguage(invoiceSettings.default_secondary_language);
      }
      if (invoiceSettings.default_notes) {
        setNotes(invoiceSettings.default_notes);
      }
      // The Settings "Default Terms & Conditions" field writes to invoice.terms,
      // which is the same column the payment-terms dropdown resolves into. It
      // used to be loaded into `terms` and then silently overwritten at submit,
      // so the setting never reached a single invoice. Seeding the custom option
      // makes it apply and leaves it visible and editable on the form.
      if (invoiceSettings.default_terms) {
        setPaymentTermsKey('other');
        setPaymentTermsCustom(invoiceSettings.default_terms);
      }
      // Calculate due date based on payment terms
      if (invoiceSettings.default_payment_terms_days) {
        const due = new Date();
        due.setDate(due.getDate() + invoiceSettings.default_payment_terms_days);
        setDueDate(due.toISOString().split('T')[0]);
      }
    }
  }, [isEditing, invoiceSettings]);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && currentInvoice) {
      setInvoiceNumber(currentInvoice.invoice_number);
      setInvoiceType(currentInvoice.invoice_type || 'standard');
      setLanguage(currentInvoice.language || 'en');
      setSecondaryLanguage(currentInvoice.secondary_language || '');
      setSellerId(currentInvoice.seller_id || null);
      setClientId(currentInvoice.client_id || null);
      setInvoiceDate(
        currentInvoice.invoice_date
          ? new Date(currentInvoice.invoice_date).toISOString().split('T')[0]
          : ''
      );
      setSaleDate(
        currentInvoice.sale_date
          ? new Date(currentInvoice.sale_date).toISOString().split('T')[0]
          : ''
      );
      setDueDate(
        currentInvoice.due_date
          ? new Date(currentInvoice.due_date).toISOString().split('T')[0]
          : ''
      );
      setPlaceOfIssue(currentInvoice.place_of_issue || '');
      setTaxRate(currentInvoice.tax_rate || 0);
      setDiscount(currentInvoice.discount || 0);
      setCurrency(currentInvoice.currency);
      setNotes(currentInvoice.notes || '');
      // Detect if stored terms matches a known option
      const storedTerms = currentInvoice.terms || '';
      const matchedKey = Object.entries(PAYMENT_TERMS_TEXT).find(([, v]) => v === storedTerms)?.[0];
      if (matchedKey) {
        setPaymentTermsKey(matchedKey);
      } else if (storedTerms) {
        setPaymentTermsKey('other');
        setPaymentTermsCustom(storedTerms);
      }
      setShowReverseChargeNotice(currentInvoice.show_reverse_charge_notice || false);

      if (currentInvoice.items && currentInvoice.items.length > 0) {
        setItems(
          currentInvoice.items.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            unit: item.unit,
            tax_rate: item.tax_rate
          }))
        );
      }
    }
  }, [isEditing, currentInvoice]);

  // Mark form as having unsaved changes when any field changes
  useEffect(() => {
    if (!isEditing) return; // Only track changes when editing existing invoice
    setHasUnsavedChanges(true);
    setAutoSaveStatus('idle');
  }, [invoiceNumber, invoiceType, language, secondaryLanguage, sellerId, clientId, invoiceDate, saleDate, dueDate, placeOfIssue, taxRate, discount, currency, notes, paymentTermsKey, paymentTermsCustom, items, isEditing]);

  // Define handleAddItem before the keyboard shortcuts useEffect that uses it
  const handleAddItem = useCallback(() => {
    setItems(prevItems => [...prevItems, { description: '', quantity: 1, unit_price: 0 }]);
  }, []);

  // Auto-save functionality
  useEffect(() => {
    if (!isEditing || !hasUnsavedChanges || saving) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(async () => {
      if (!id) return;

      try {
        setAutoSaveStatus('saving');
        const validItems = items.filter((item) => item.description.trim());

        if (validItems.length === 0) {
          setAutoSaveStatus('idle');
          return;
        }

        await dispatch(updateInvoice({
          id: parseInt(id),
          data: {
            // A half-cleared number field must not fail the whole auto-save;
            // the explicit save is where an empty number gets flagged.
            invoice_number: invoiceNumber.trim() || undefined,
            invoice_type: invoiceType,
            language: language,
            secondary_language: secondaryLanguage || undefined,
            seller_id: sellerId || undefined,
            client_id: clientId || undefined,
            invoice_date: invoiceDate || undefined,
            sale_date: saleDate || undefined,
            due_date: dueDate || undefined,
            place_of_issue: placeOfIssue || undefined,
            tax_rate: taxRate,
            discount: discount,
            currency: currency,
            notes: notes || undefined,
            terms: paymentTermsKey === 'other' ? (paymentTermsCustom || undefined) : (PAYMENT_TERMS_TEXT[paymentTermsKey] || undefined),
            show_reverse_charge_notice: showReverseChargeNotice,
            items: validItems.map((item) => ({
              description: item.description,
              quantity: item.quantity,
              unit_price: item.unit_price,
              unit: item.unit,
              tax_rate: item.tax_rate
            }))
          }
        })).unwrap();

        setAutoSaveStatus('saved');
        setHasUnsavedChanges(false);

        // Reset to idle after 3 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } catch (err) {
        setAutoSaveStatus('error');
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, isEditing, id, saving, dispatch, invoiceNumber, invoiceType, language, secondaryLanguage, sellerId, clientId, invoiceDate, saleDate, dueDate, placeOfIssue, taxRate, discount, currency, notes, paymentTermsKey, paymentTermsCustom, items]);

  // The error banner sits above the form; a save that fails while the user is
  // down at the line items would otherwise look like a button that did nothing.
  useEffect(() => {
    if (formError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formError]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        // Only allow Ctrl+S even in inputs
        if (!(e.ctrlKey && e.key === 's')) {
          return;
        }
      }

      // Ctrl+S: Save
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }

      // Ctrl+N: Add new item
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        handleAddItem();
      }

      // Escape: Go back
      if (e.key === 'Escape' && !showClientModal) {
        navigate('/created-invoices');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showClientModal, handleAddItem]);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount - discount;

  // `usage === null` means the usage fetch has not landed (or failed) — it does
  // NOT mean the limit is reached. Treating it as "blocked" showed a full-page
  // "Creation Limit Reached" screen and a false "You have reached your PDF
  // download limit" banner to users who were nowhere near their limit. The
  // backend enforces the real limit on every request, so optimistically
  // allowing here is safe.
  const canEdit = usage ? usage.invoices_edited < usage.invoices_edited_limit : true;
  const canCreate = usage ? usage.invoices_created < usage.invoices_created_limit : true;

  // A new invoice whose number field still shows the preview (or is empty)
  // leaves the number to the server. The server compares case-insensitively,
  // so this does too.
  const trimmedNumber = invoiceNumber.trim();
  const numberIsSuggested = !isEditing
    && (!trimmedNumber || trimmedNumber.toLowerCase() === suggestedNumber.toLowerCase());
  // Once submitted to KSeF the number is part of a filed document; the server
  // refuses to change it as well.
  const ksefStatus = currentInvoice?.ksef?.status;
  const numberLocked = isEditing && (ksefStatus === 'processing' || ksefStatus === 'accepted');
  const numberHint = numberLocked
    ? "Submitted to KSeF under this number, so it can't be changed."
    : isEditing
      ? null
      : numberIsSuggested
        ? 'Assigned when you save: the next free number in your series.'
        : suggestedNumber
          ? `Custom number. Your series continues at ${suggestedNumber}.`
          : 'Custom number.';

  const currencySymbol = CURRENCIES.find(c => c.value === currency)?.symbol || '$';

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, field: keyof FormItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      return;
    }

    try {
      setCreatingClient(true);
      const clientData: ClientCreateData = {
        name: newClientName.trim(),
        email: newClientEmail.trim() || undefined,
        phone: newClientPhone.trim() || undefined,
        is_business: newClientIsBusiness,
        tax_id: newClientTaxId.trim() || undefined,
        street: newClientStreet.trim() || undefined,
        street_number: newClientStreetNumber.trim() || undefined,
        city: newClientCity.trim() || undefined,
        postal_code: newClientPostalCode.trim() || undefined,
        country: newClientCountry.trim() || undefined
      };

      const result = await dispatch(createClient(clientData)).unwrap();
      setClientId(result.id);
      setShowClientModal(false);
      // Reset form
      setNewClientName('');
      setNewClientEmail('');
      setNewClientPhone('');
      setNewClientIsBusiness(true);
      setNewClientTaxId('');
      setNewClientStreet('');
      setNewClientStreetNumber('');
      setNewClientCity('');
      setNewClientPostalCode('');
      setNewClientCountry('');
    } catch (err) {
      console.error('Failed to create client:', err);
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setFormError(null);

    // Validation. A new invoice may leave the number empty: the server assigns one.
    if (isEditing && !trimmedNumber) {
      setFormError('Invoice number is required');
      return;
    }

    if (items.every((item) => !item.description.trim())) {
      setFormError('At least one item is required');
      return;
    }

    const validItems = items.filter((item) => item.description.trim());

    try {
      submittingRef.current = true;
      setSaving(true);

      const resolvedTerms = paymentTermsKey === 'other'
        ? (paymentTermsCustom || undefined)
        : (PAYMENT_TERMS_TEXT[paymentTermsKey] || undefined);

      const invoiceData = {
        invoice_number: numberIsSuggested ? undefined : trimmedNumber,
        invoice_type: invoiceType,
        language: language,
        secondary_language: secondaryLanguage || undefined,
        seller_id: sellerId || undefined,
        client_id: clientId || undefined,
        invoice_date: invoiceDate || undefined,
        sale_date: saleDate || undefined,
        due_date: dueDate || undefined,
        place_of_issue: placeOfIssue || undefined,
        tax_rate: taxRate,
        discount: discount,
        currency: currency,
        notes: notes || undefined,
        terms: resolvedTerms,
        show_reverse_charge_notice: showReverseChargeNotice,
        items: validItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit,
          tax_rate: item.tax_rate
        }))
      };

      if (isEditing && id) {
        await dispatch(updateInvoice({ id: parseInt(id), data: invoiceData })).unwrap();
      } else {
        const created = await dispatch(createInvoice(invoiceData)).unwrap();
        // The field showed a preview; say so when the saved number differs.
        if (numberIsSuggested && suggestedNumber && created.invoice_number !== suggestedNumber) {
          toast.info(
            `Saved as ${created.invoice_number}: ${suggestedNumber} was no longer the next free number.`
          );
        }
      }

      navigate('/created-invoices');
    } catch (err: any) {
      const errorMessage = typeof err === 'string'
        ? err
        : err?.message || err?.detail || JSON.stringify(err) || 'Failed to save invoice';
      setFormError(errorMessage);
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  // Check usage limits
  if (isEditing && !canEdit) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="p-6 bg-orange-500/10 border border-orange-500/20 rounded-lg text-center">
            <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-orange-200 mb-2">Edit Limit Reached</h2>
            <p className="text-orange-300 mb-4">
              You have reached your invoice edit limit for this period.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors"
            >
              Upgrade Your Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isEditing && !canCreate) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="p-6 bg-orange-500/10 border border-orange-500/20 rounded-lg text-center">
            <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-orange-200 mb-2">Creation Limit Reached</h2>
            <p className="text-orange-300 mb-4">
              You have reached your invoice creation limit for this period.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors"
            >
              Upgrade Your Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/created-invoices')}
            className="p-2 hover:bg-coral-500/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-coral-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 dark:text-white">
              {isEditing ? 'Edit Invoice' : 'New Invoice'}
            </h1>
            <p className="text-xs text-coral-400 mt-1">
              Ctrl+S to save | Ctrl+N to add item | Esc to cancel
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Auto-save status indicator */}
          {isEditing && (
            <div className="flex items-center gap-2 text-sm">
              {autoSaveStatus === 'saving' && (
                <>
                  <Clock className="w-4 h-4 text-yellow-400 animate-pulse" />
                  <span className="text-yellow-400">Saving...</span>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">Saved</span>
                </>
              )}
              {autoSaveStatus === 'error' && (
                <>
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400">Auto-save failed</span>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate('/created-invoices/settings')}
            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-muted hover:bg-coral-500/10 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Error display */}
      {(error || formError) && (
        <div ref={errorRef} className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <AlertTriangle className="w-5 h-5" />
            <span>{formError || error}</span>
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Type & Language */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {t('invoice_type')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('type')}</label>
                    <select
                      value={invoiceType}
                      onChange={(e) => {
                        const type = e.target.value as InvoiceType;
                        setInvoiceType(type);
                        // Reverse charge is tax-exempt by definition; taxExempt
                        // is derived from this pair, not tracked separately.
                        if (type === 'reverse_charge') setTaxRate(0);
                      }}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    >
                      {INVOICE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('place_of_issue')}</label>
                    <input
                      type="text"
                      value={placeOfIssue}
                      onChange={(e) => setPlaceOfIssue(e.target.value)}
                      placeholder="City"
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">
                      <Globe className="w-4 h-4 inline-block mr-1" />
                      {t('primary_language')}
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value as InvoiceLanguage)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    >
                      {LANGUAGES.map((lang) => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">
                      <Globe className="w-4 h-4 inline-block mr-1" />
                      {t('secondary_language')}
                    </label>
                    <select
                      value={secondaryLanguage}
                      onChange={(e) => setSecondaryLanguage(e.target.value as InvoiceLanguage | '')}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    >
                      <option value="">None (Single language)</option>
                      {LANGUAGES.filter(lang => lang.value !== language).map((lang) => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                    {secondaryLanguage && (
                      <p className="text-xs text-coral-400 mt-1">
                        Labels will show as: Primary / Secondary
                      </p>
                    )}
                  </div>
                </div>

                {/* Live Invoice Title Preview */}
                <div className="mt-4 p-3 bg-coral-500/10 border border-coral-500/20 rounded-lg">
                  <p className="text-xs text-coral-400 mb-1">Invoice Title Preview:</p>
                  <p className="text-lg font-semibold text-foreground">
                    {getInvoiceTypeTitle(invoiceType, language, secondaryLanguage)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Seller Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-foreground flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {t('from')} ({t('seller')})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sellers.length === 0 ? (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <p className="text-orange-300 text-sm mb-2">
                      No seller profiles found. Create one in settings first.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/created-invoices/settings')}
                      className="text-sm text-coral-400 hover:text-foreground"
                    >
                      Go to Settings
                    </button>
                  </div>
                ) : (
                  <select
                    value={sellerId || ''}
                    onChange={(e) => setSellerId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                  >
                    <option value="">{t('select_seller')}</option>
                    {sellers.map((seller) => (
                      <option key={seller.id} value={seller.id}>
                        {seller.name} {seller.is_default ? '(Default)' : ''} {seller.tax_id ? `- ${seller.tax_id}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </CardContent>
            </Card>

            {/* Invoice details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-foreground">{t('invoice_details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">
                      {t('invoice_number')}{isEditing ? ' *' : ''}
                    </label>
                    <input
                      type="text"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder={isEditing ? undefined : suggestedNumber}
                      readOnly={numberLocked}
                      maxLength={50}
                      className={`w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40${numberLocked ? ' opacity-60 cursor-not-allowed' : ''}`}
                      required={isEditing}
                    />
                    {numberHint && (
                      <p className="text-xs text-coral-400 mt-1">{numberHint}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('client')} ({t('bill_to')})</label>
                    <div className="flex gap-2">
                      <select
                        value={clientId || ''}
                        onChange={(e) => setClientId(e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                      >
                        <option value="">{t('select_client')}</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.is_favorite ? '★ ' : ''}{client.name}
                            {client.is_business && client.tax_id ? ` (${client.tax_id})` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowClientModal(true)}
                        className="px-3 py-2 bg-coral-500/20 border border-coral-500/20 rounded-lg text-foreground-muted hover:bg-coral-500/30 transition-colors"
                        title="Add new client"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('invoice_date')}</label>
                    <input
                      type="date"
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('sale_date')}</label>
                    <input
                      type="date"
                      value={saleDate}
                      onChange={(e) => setSaleDate(e.target.value)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('due_date')}</label>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-foreground">{t('items')}</CardTitle>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-coral-500/20 text-foreground-muted rounded-lg hover:bg-coral-500/30 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('add_item')}
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Header row */}
                <div className="hidden md:grid grid-cols-12 gap-3 mb-2 px-3 text-xs text-coral-400 uppercase tracking-wider">
                  <div className="col-span-4">{t('description')}</div>
                  <div className="col-span-2">{t('quantity')}</div>
                  <div className="col-span-1">{t('unit')}</div>
                  <div className="col-span-2">{t('unit_price')}</div>
                  <div className="col-span-2 text-right">{t('total')}</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 gap-2 items-center p-3 bg-coral-500/5 rounded-lg"
                    >
                      <div className="col-span-12 md:col-span-4">
                        <input
                          type="text"
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground text-sm focus:outline-none focus:border-coral-500/40"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) =>
                            handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)
                          }
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground text-sm focus:outline-none focus:border-coral-500/40"
                        />
                      </div>
                      <div className="col-span-3 md:col-span-1">
                        <select
                          value={item.unit || ''}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value || undefined)}
                          className="w-full px-2 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground text-sm focus:outline-none focus:border-coral-500/40"
                        >
                          {UNITS.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-5 md:col-span-2">
                        <input
                          type="number"
                          placeholder="Price"
                          value={item.unit_price}
                          onChange={(e) =>
                            handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                          }
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground text-sm focus:outline-none focus:border-coral-500/40"
                        />
                      </div>
                      <div className="col-span-10 md:col-span-2 flex items-center justify-end">
                        <span className="text-foreground-secondary text-sm font-medium">
                          {currencySymbol}{(item.quantity * item.unit_price).toFixed(2)}
                        </span>
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                          className={`p-2 rounded-lg transition-colors ${
                            items.length === 1
                              ? 'text-coral-500/30 cursor-not-allowed'
                              : 'text-red-400 hover:bg-red-500/20'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment terms & additional info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-foreground">{t('additional_info')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Payment terms dropdown */}
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('payment_terms')}</label>
                    <select
                      value={paymentTermsKey}
                      onChange={(e) => setPaymentTermsKey(e.target.value)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    >
                      {PAYMENT_TERMS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {paymentTermsKey === 'other' && (
                      <textarea
                        value={paymentTermsCustom}
                        onChange={(e) => setPaymentTermsCustom(e.target.value)}
                        rows={2}
                        placeholder="Enter custom payment terms..."
                        className="w-full mt-2 px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40 resize-none"
                      />
                    )}
                  </div>

                  {/* Notes (optional) */}
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('notes')} <span className="text-coral-500">(optional)</span></label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Optional notes for the client (will appear on the invoice)..."
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40 resize-none"
                    />
                  </div>

                  {/* Reverse charge notice checkbox (only for RC invoices) */}
                  {invoiceType === 'reverse_charge' && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showReverseChargeNotice}
                          onChange={(e) => setShowReverseChargeNotice(e.target.checked)}
                          className="mt-0.5 accent-coral-500"
                        />
                        <div>
                          <span className="text-sm text-orange-200 font-medium">
                            Include reverse charge legal notice on PDF
                          </span>
                          <p className="text-xs text-orange-300/70 mt-1 italic">
                            "Reverse charge – VAT to be accounted for by the recipient as per Article 196 of the VAT Directive / Odwrotne obciążenie – VAT rozlicza nabywca zgodnie z art. 17 ust. 1 pkt 7 lub 8 ustawy o VAT"
                          </p>
                        </div>
                      </label>
                      {showReverseChargeNotice && (
                        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                          <p className="text-xs text-yellow-300">
                            <strong>⚠ Disclaimer:</strong> This legal notice is provided for informational purposes only.
                            You are solely responsible for verifying that this notice is legally correct and applicable to your
                            specific transaction and jurisdiction. ViewPoint does not provide legal or tax advice.
                            Consult a qualified tax professional before including this notice on your invoices.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - totals */}
          <div className="space-y-6">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">{t('summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('currency')}</label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    >
                      {CURRENCIES.map((curr) => (
                        <option key={curr.value} value={curr.value}>
                          {curr.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('tax_rate')} (%)</label>
                    {invoiceType === 'reverse_charge' ? (
                      <select
                        value={String(taxRate)}
                        onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                      >
                        <option value="0">{t('tax_exempt')}</option>
                        <option value="5">5%</option>
                        <option value="8">8%</option>
                        <option value="19">19%</option>
                        <option value="23">23%</option>
                      </select>
                    ) : (
                      <input
                        type="number"
                        value={taxRate}
                        onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-muted mb-2">{t('discount')}</label>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    />
                  </div>

                  <div className="pt-4 border-t border-coral-500/20 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-coral-400">{t('subtotal')}</span>
                      <span className="text-foreground-secondary">{currencySymbol}{subtotal.toFixed(2)}</span>
                    </div>
                    {taxExempt ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-coral-400">{t('tax')}</span>
                        <span className="text-foreground-secondary">{t('tax_exempt')} —</span>
                      </div>
                    ) : taxRate > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-coral-400">{t('tax')} ({taxRate}%)</span>
                        <span className="text-foreground-secondary">{currencySymbol}{taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-coral-400">{t('discount')}</span>
                        <span className="text-red-300">-{currencySymbol}{discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-semibold pt-2 border-t border-coral-500/20">
                      <span className="text-foreground-secondary">{t('total')}</span>
                      <span className="text-foreground">{currencySymbol}{total.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={saving || loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 text-white rounded-lg transition-colors"
                  >
                    {saving ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    {isEditing ? t('update_invoice') : t('create_invoice')}
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* New client modal */}
      {showClientModal && (
        <Modal
          open
          onClose={() => setShowClientModal(false)}
          size="md"
          title="Add New Client"
          footer={
            <>
              <button
                onClick={() => setShowClientModal(false)}
                className="px-4 py-2 text-foreground-muted hover:bg-coral-500/10 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                disabled={!newClientName.trim() || creatingClient}
                className="flex items-center gap-2 px-4 py-2 bg-coral-500 hover:bg-coral-600 disabled:bg-coral-500/50 text-white rounded-lg transition-colors text-sm"
              >
                {creatingClient ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add Client
              </button>
            </>
          }
        >
            <div className="space-y-4">
              {/* Client Type Toggle */}
              <div>
                <label className="block text-sm text-foreground-muted mb-2">Client Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewClientIsBusiness(true)}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                      newClientIsBusiness
                        ? 'bg-coral-500/20 border-coral-500 text-foreground'
                        : 'bg-coral-500/5 border-coral-500/20 text-coral-400'
                    }`}
                  >
                    <Building2 className="w-4 h-4 inline-block mr-2" />
                    Business
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewClientIsBusiness(false)}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                      !newClientIsBusiness
                        ? 'bg-coral-500/20 border-coral-500 text-foreground'
                        : 'bg-coral-500/5 border-coral-500/20 text-coral-400'
                    }`}
                  >
                    <User className="w-4 h-4 inline-block mr-2" />
                    Individual
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-foreground-muted mb-2">
                  {newClientIsBusiness ? 'Company Name' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                  placeholder={newClientIsBusiness ? 'Company Ltd.' : 'John Doe'}
                />
              </div>

              {newClientIsBusiness && (
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    value={newClientTaxId}
                    onChange={(e) => setNewClientTaxId(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="VAT123456789"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">Email</label>
                  <input
                    type="email"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-foreground-muted mb-2">Street</label>
                  <input
                    type="text"
                    value={newClientStreet}
                    onChange={(e) => setNewClientStreet(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="Main Street"
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">Number</label>
                  <input
                    type="text"
                    value={newClientStreetNumber}
                    onChange={(e) => setNewClientStreetNumber(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="123"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">City</label>
                  <input
                    type="text"
                    value={newClientCity}
                    onChange={(e) => setNewClientCity(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="New York"
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-muted mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={newClientPostalCode}
                    onChange={(e) => setNewClientPostalCode(e.target.value)}
                    className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                    placeholder="10001"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-foreground-muted mb-2">Country</label>
                <input
                  type="text"
                  value={newClientCountry}
                  onChange={(e) => setNewClientCountry(e.target.value)}
                  className="w-full px-3 py-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground focus:outline-none focus:border-coral-500/40"
                  placeholder="United States"
                />
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoiceForm;
